import {
  CronDayOfWeek,
  CronSchedulingPolicy,
  DEFAULT_NIGHTLY_CRON,
  SchedulingPolicy,
  DEFAULT_WEEKLY_CRON_PREFIX,
} from '@ardoq/api-types';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { format, currentDate } from '@ardoq/date-time';
import {
  HOUR_OPTIONS,
  HourOptionValue,
  ScheduleState,
  WeekOptionValues,
} from './types';

const VALID_CRON_DAYS: readonly CronDayOfWeek[] = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
];

const VALID_CRON_HOURS: readonly HourOptionValue[] = HOUR_OPTIONS.map(
  o => o.value
);

const DEFAULT_NIGHTLY_HOUR: HourOptionValue = '0';

function isValidCronDay(value: string | undefined): value is CronDayOfWeek {
  return (
    value !== undefined && VALID_CRON_DAYS.includes(value as CronDayOfWeek)
  );
}

function isValidCronHour(value: string | undefined): value is HourOptionValue {
  return (
    value !== undefined && VALID_CRON_HOURS.includes(value as HourOptionValue)
  );
}

export function formatUtcHourToLocalTime(utcHour: HourOptionValue): string {
  const localDate = currentDate();
  localDate.setUTCHours(parseInt(utcHour, 10));
  localDate.setUTCMinutes(0);
  return format(localDate, 'HH:mm');
}

function weekDayToCronDay(weekday: WeekOptionValues): CronDayOfWeek {
  switch (weekday) {
    case 'sunday':
      return '0';
    case 'monday':
      return '1';
    case 'tuesday':
      return '2';
    case 'wednesday':
      return '3';
    case 'thursday':
      return '4';
    case 'friday':
      return '5';
    case 'saturday':
      return '6';
  }
  return exhaustiveCheck(weekday);
}

export function migrateDeprecatedSchedulingPolicy(
  policy: SchedulingPolicy
): CronSchedulingPolicy {
  switch (policy.type) {
    case 'cron':
      return policy;
    case 'nightly':
      return {
        type: 'cron',
        cronExpression: DEFAULT_NIGHTLY_CRON,
      };
    case 'weekly':
      return {
        type: 'cron',
        cronExpression: `${DEFAULT_WEEKLY_CRON_PREFIX}${weekDayToCronDay(policy.weekDay)}`,
      };
  }
}

function cronDayToWeekDay(weekday: CronDayOfWeek): WeekOptionValues {
  switch (weekday) {
    case '0':
      return 'sunday';
    case '1':
      return 'monday';
    case '2':
      return 'tuesday';
    case '3':
      return 'wednesday';
    case '4':
      return 'thursday';
    case '5':
      return 'friday';
    case '6':
      return 'saturday';
  }
  return exhaustiveCheck(weekday);
}

export function cronPolicyToScheduleState(
  policy: CronSchedulingPolicy
): ScheduleState {
  const cronParts = policy.cronExpression.split(' ');
  const hourPart = cronParts[1];
  const weekDayPart = cronParts[4];

  const hour = isValidCronHour(hourPart) ? hourPart : DEFAULT_NIGHTLY_HOUR;

  // Check if it's a weekly schedule (has a specific day of week)
  if (isValidCronDay(weekDayPart)) {
    return {
      scheduleType: 'weekly',
      weekDay: cronDayToWeekDay(weekDayPart),
      hour,
    };
  }

  // Default to nightly schedule (runs every day)
  return {
    scheduleType: 'nightly',
    hour,
  };
}

export function scheduleStateToCronExpression(
  state: ScheduleState
): CronSchedulingPolicy['cronExpression'] {
  switch (state.scheduleType) {
    case 'nightly':
      return `0 ${state.hour} * * *`;
    case 'weekly':
      return `0 ${state.hour} * * ${weekDayToCronDay(state.weekDay)}`;
  }
  return exhaustiveCheck(state);
}
